/**
 * Sync Queue Manager
 * Handles syncing offline data when connectivity is restored
 */

import {
  getPendingIncidents,
  updateIncidentSyncStatus,
  getPendingSyncOperations,
  updateSyncQueueItem,
  clearCompletedSyncItems,
  getSyncStats,
  initDB
} from './offlineStorage';

// Event emitter for sync status updates
const listeners = new Set();

/**
 * Subscribe to sync status updates
 */
export const onSyncStatusChange = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

/**
 * Notify all listeners of status change
 */
const notifyListeners = (status) => {
  listeners.forEach(callback => callback(status));
};

/**
 * Check if we're online
 */
export const isOnline = () => navigator.onLine;

/**
 * Batch sync incidents to the server using the /api/incidents/sync endpoint
 */
const batchSyncIncidents = async (incidents, token) => {
  const response = await fetch('/api/incidents/sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      incidents: incidents.map(inc => ({
        localId: inc.localId,
        type: inc.type,
        incidentType: inc.incidentType || inc.type,
        location: inc.location,
        locationDescription: inc.locationDescription || inc.location,
        description: inc.description,
        latitude: inc.latitude,
        longitude: inc.longitude,
        notes: inc.notes,
        freeway: inc.freeway,
        source: 'OFFLINE-MDT',
        offlineCreatedAt: inc.createdAt,
        occurredAtUtc: inc.reportedAt || inc.createdAt
      }))
    })
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Batch sync failed: ${error}`);
  }

  return await response.json();
};

/**
 * Sync a single incident to the server (fallback method)
 */
const syncIncident = async (incident, token) => {
  const response = await fetch('/api/incidents', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      incidentType: incident.type || incident.incidentType,
      locationDescription: incident.location || incident.locationDescription,
      latitude: incident.latitude,
      longitude: incident.longitude,
      notes: incident.notes,
      occurredAtUtc: incident.reportedAt || incident.createdAt || new Date().toISOString()
    })
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to sync incident: ${error}`);
  }

  return await response.json();
};

/**
 * Process a sync queue operation
 */
const processSyncOperation = async (operation, token) => {
  const { type, method, url, body } = operation;

  const response = await fetch(url, {
    method: method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: body ? JSON.stringify(body) : undefined
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Sync operation failed: ${error}`);
  }

  return response.status !== 204 ? await response.json() : null;
};

/**
 * Sync all pending incidents using batch endpoint
 */
export const syncPendingIncidents = async (token) => {
  if (!isOnline()) {
    console.log('Offline - skipping incident sync');
    return { synced: 0, failed: 0 };
  }

  await initDB();
  const pendingIncidents = await getPendingIncidents();
  
  if (pendingIncidents.length === 0) {
    return { synced: 0, failed: 0 };
  }

  console.log(`Syncing ${pendingIncidents.length} pending incidents...`);
  notifyListeners({ type: 'syncing', message: `Syncing ${pendingIncidents.length} incidents...` });

  // Mark all as syncing
  for (const incident of pendingIncidents) {
    await updateIncidentSyncStatus(incident.localId, 'syncing');
  }

  let synced = 0;
  let failed = 0;

  try {
    // Try batch sync first
    const result = await batchSyncIncidents(pendingIncidents, token);
    
    // Process results
    for (const syncResult of result.results || []) {
      if (syncResult.success) {
        await updateIncidentSyncStatus(syncResult.localId, 'synced', syncResult.serverId);
        synced++;
        console.log(`Incident ${syncResult.localId} synced as server ID ${syncResult.serverId}, CAD ${syncResult.cadId}`);
      } else {
        await updateIncidentSyncStatus(syncResult.localId, 'error');
        failed++;
        console.error(`Failed to sync incident ${syncResult.localId}: ${syncResult.message}`);
      }
    }
  } catch (batchError) {
    console.warn('Batch sync failed, falling back to individual sync:', batchError);
    
    // Fallback to individual sync
    for (const incident of pendingIncidents) {
      try {
        const serverIncident = await syncIncident(incident, token);
        await updateIncidentSyncStatus(incident.localId, 'synced', serverIncident.id);
        synced++;
        console.log(`Incident ${incident.localId} synced as server ID ${serverIncident.id}`);
      } catch (error) {
        console.error(`Failed to sync incident ${incident.localId}:`, error);
        await updateIncidentSyncStatus(incident.localId, 'error');
        failed++;
      }
    }
  }

  notifyListeners({ 
    type: 'sync-complete', 
    message: `Synced ${synced} incidents, ${failed} failed`,
    synced,
    failed
  });

  return { synced, failed };
};

/**
 * Process all pending sync queue operations
 */
export const processSyncQueue = async (token) => {
  if (!isOnline()) {
    console.log('Offline - skipping sync queue processing');
    return { processed: 0, failed: 0 };
  }

  await initDB();
  const pendingOps = await getPendingSyncOperations();
  
  if (pendingOps.length === 0) {
    return { processed: 0, failed: 0 };
  }

  console.log(`Processing ${pendingOps.length} sync queue operations...`);
  notifyListeners({ type: 'syncing', message: `Processing ${pendingOps.length} queued operations...` });

  let processed = 0;
  let failed = 0;

  for (const operation of pendingOps) {
    try {
      await updateSyncQueueItem(operation.id, { status: 'processing' });
      await processSyncOperation(operation, token);
      await updateSyncQueueItem(operation.id, { status: 'completed' });
      processed++;
    } catch (error) {
      console.error(`Failed to process sync operation ${operation.id}:`, error);
      await updateSyncQueueItem(operation.id, { 
        status: 'failed',
        error: error.message,
        retryCount: (operation.retryCount || 0) + 1
      });
      failed++;
    }
  }

  // Clean up completed items
  await clearCompletedSyncItems();

  notifyListeners({ 
    type: 'sync-complete', 
    message: `Processed ${processed} operations, ${failed} failed`,
    processed,
    failed
  });

  return { processed, failed };
};

/**
 * Run full sync (incidents + queue)
 */
export const runFullSync = async (token) => {
  if (!isOnline()) {
    notifyListeners({ type: 'offline', message: 'No internet connection' });
    return null;
  }

  notifyListeners({ type: 'sync-start', message: 'Starting sync...' });

  try {
    const incidentResults = await syncPendingIncidents(token);
    const queueResults = await processSyncQueue(token);

    const stats = await getSyncStats();
    
    notifyListeners({ 
      type: 'sync-complete', 
      message: 'Sync complete',
      results: {
        incidents: incidentResults,
        queue: queueResults,
        stats
      }
    });

    return { incidents: incidentResults, queue: queueResults, stats };
  } catch (error) {
    console.error('Sync error:', error);
    notifyListeners({ type: 'sync-error', message: error.message });
    throw error;
  }
};

/**
 * Initialize sync manager with automatic online/offline handling
 */
export const initSyncManager = (getToken) => {
  // Initialize database
  initDB().catch(console.error);

  // Handle coming back online
  const handleOnline = async () => {
    console.log('Connection restored - starting sync');
    notifyListeners({ type: 'online', message: 'Connection restored' });
    
    try {
      const token = getToken();
      if (token) {
        await runFullSync(token);
      }
    } catch (error) {
      console.error('Auto-sync failed:', error);
    }
  };

  // Handle going offline
  const handleOffline = () => {
    console.log('Connection lost - entering offline mode');
    notifyListeners({ type: 'offline', message: 'Working offline' });
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Initial status
  if (!isOnline()) {
    notifyListeners({ type: 'offline', message: 'Working offline' });
  }

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

/**
 * Request background sync (if supported)
 */
export const requestBackgroundSync = async () => {
  const reg = window.swRegistration || window.registration;
  if ('serviceWorker' in navigator && reg && 'sync' in reg) {
    try {
      await reg.sync.register('sync-incidents');
      console.log('Background sync registered');
    } catch (error) {
      console.error('Background sync registration failed:', error);
    }
  }
};

export default {
  isOnline,
  syncPendingIncidents,
  processSyncQueue,
  runFullSync,
  initSyncManager,
  onSyncStatusChange,
  requestBackgroundSync
};
